import {
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react';
import { CumulativePremiumV2Widget } from '../../components/CumulativePremiumV2Widget';
import { GexProfileWidget } from '../../components/GexProfileWidget';
import { GexRayWidget } from '../../components/GexRayWidget';
import { PermissionGate } from '../../components/PermissionGate';
import { TDSequentialWidget } from '../../components/TDSequentialWidget';
import { AnalystRatingsSection } from '../../components/TickerDetail/AnalystRatingsSection';
import { CongressTradesSection } from '../../components/TickerDetail/CongressTradesSection';
import { Dividends } from '../../components/TickerDetail/Dividends';
import { Earnings } from '../../components/TickerDetail/Earnings';
import { Financials } from '../../components/TickerDetail/Financials';
import { GammaSection } from '../../components/TickerDetail/GammaSection';
import { HeroBar } from '../../components/TickerDetail/HeroBar';
import { InsiderTradesSection } from '../../components/TickerDetail/InsiderTradesSection';
import { InstitutionalHolders } from '../../components/TickerDetail/InstitutionalHolders';
import { Overview } from '../../components/TickerDetail/Overview';
import { useTickerLink } from '../../contexts/TickerLinkContext';
import type { ToolType } from '../../types/dashboard';
import { isIndexSymbol } from '../../utils/tickerDisplay';
import { mobileTickerStore } from '../mobileTickerStore';
import { useHubPane } from '../hubPrefs';

/** Fixed windowIds for mobile-hosted dashboard widgets (Family-A prefs). */
const WIN = {
  gexray: 'mobile-gex-ray',
  premium: 'mobile-cumulative-premium',
  gexProfile: 'mobile-gex-profile',
  niners: 'mobile-td-sequential',
} as const;

type HubId =
  | 'overview'
  | 'gamma'
  | 'gexray'
  | 'premium'
  | 'gexprofile'
  | 'niners'
  | 'ratings'
  | 'financials'
  | 'flows'
  | 'earnings'
  | 'institutions';

interface HubChip {
  id: HubId;
  label: string;
  tool?: ToolType;
  /** Hidden for cash indices (no company data). */
  companyOnly?: boolean;
}

const HUB_CHIPS: readonly HubChip[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'gamma', label: 'Gamma', tool: 'gex-ray' },
  { id: 'gexray', label: 'GexRay', tool: 'gex-ray' },
  { id: 'premium', label: 'Net Sweep', tool: 'cumulative-premium' },
  { id: 'gexprofile', label: 'GEX', tool: 'gex-profile' },
  { id: 'niners', label: 'Niners', tool: 'td-sequential' },
  { id: 'ratings', label: 'Ratings', tool: 'upgrades-downgrades', companyOnly: true },
  { id: 'financials', label: 'Financials', companyOnly: true },
  { id: 'flows', label: 'Flows', tool: 'congress-trades', companyOnly: true },
  { id: 'earnings', label: 'Earnings', companyOnly: true },
  { id: 'institutions', label: 'Inst', companyOnly: true },
];

/** Every hub id, for validating persisted selections. */
const HUB_IDS = HUB_CHIPS.map((c) => c.id) as readonly HubId[];

function noop(): void {
  /* dashboard remove is irrelevant on mobile */
}

function SectionGate({
  tool,
  children,
}: {
  tool?: ToolType;
  children: ReactNode;
}): ReactElement {
  if (!tool) return <>{children}</>;
  return <PermissionGate toolType={tool}>{children}</PermissionGate>;
}

/**
 * Ticker Explorer hub — shared symbol + chips for company sections and
 * dashboard tools (GexRay, Net Sweep, GEX Profile, Niners).
 */
export function MobileExplorerTab(): ReactElement {
  const symbol = useSyncExternalStore(
    mobileTickerStore.subscribe,
    mobileTickerStore.getSnapshot,
  );
  const { setLinkedTicker, isWindowLinked, toggleWindowLink } = useTickerLink();
  const [hub, setHub] = useHubPane<HubId>('explorerHub', HUB_IDS, 'overview');
  const [input, setInput] = useState(symbol);

  useEffect(() => {
    setInput(symbol);
  }, [symbol]);

  // Push explorer symbol into ticker-link bus so linked widgets follow.
  useEffect(() => {
    if (symbol) setLinkedTicker(symbol);
  }, [symbol, setLinkedTicker]);

  // Ensure mobile widget panes are linked so they consume linkedTicker.
  useEffect(() => {
    for (const id of Object.values(WIN)) {
      if (!isWindowLinked(id)) {
        toggleWindowLink(id);
      }
    }
    // Once on mount — linking is sticky in frontend_state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isIndex = isIndexSymbol(symbol);
  const chips = HUB_CHIPS.filter((c) => !(c.companyOnly && isIndex));
  const active: HubId = chips.some((c) => c.id === hub) ? hub : 'overview';
  const chipMeta = chips.find((c) => c.id === active);

  const applySymbol = (raw: string) => {
    const next = raw.trim().toUpperCase();
    if (!next) return;
    mobileTickerStore.setSymbol(next);
  };

  const widgetFill =
    active === 'gexray' ||
    active === 'premium' ||
    active === 'gexprofile' ||
    active === 'niners';

  return (
    <div className="sr-mobile-hub">
      <label className="sr-mobile-explorer__search">
        <span className="sr-mobile-explorer__label">Ticker</span>
        <input
          className="sr-mobile-explorer__input"
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          onBlur={() => applySymbol(input)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applySymbol(input);
              (e.target as HTMLInputElement).blur();
            }
          }}
          aria-label="Symbol"
        />
      </label>

      <div className="sr-mobile-hub-chips" role="tablist" aria-label="Explorer tools">
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            className="sr-mobile-hub-chip"
            aria-selected={active === c.id}
            onClick={() => setHub(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div
        className={
          widgetFill
            ? 'sr-mobile-hub-stage sr-mobile-hub-stage--widget'
            : 'sr-mobile-hub-stage sr-mobile-hub-stage--scroll'
        }
      >
        <SectionGate tool={chipMeta?.tool}>
          {active === 'overview' && (
            <>
              <HeroBar symbol={symbol} />
              <Overview symbol={symbol} />
            </>
          )}
          {active === 'gamma' && (
            <>
              <HeroBar symbol={symbol} />
              <GammaSection symbol={symbol} />
            </>
          )}
          {active === 'ratings' && <AnalystRatingsSection symbol={symbol} />}
          {active === 'financials' && (
            <>
              <Financials symbol={symbol} />
              <Dividends symbol={symbol} />
            </>
          )}
          {active === 'flows' && (
            <>
              <CongressTradesSection symbol={symbol} />
              <InsiderTradesSection symbol={symbol} />
            </>
          )}
          {active === 'earnings' && <Earnings symbol={symbol} />}
          {active === 'institutions' && <InstitutionalHolders symbol={symbol} />}

          {active === 'gexray' && (
            <div
              className="sr-mobile-widget-host"
              style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
              <GexRayWidget windowId={WIN.gexray} onRemove={noop} />
            </div>
          )}
          {active === 'premium' && (
            <div
              className="sr-mobile-widget-host"
              style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
              <CumulativePremiumV2Widget windowId={WIN.premium} onRemove={noop} />
            </div>
          )}
          {active === 'gexprofile' && (
            <div
              className="sr-mobile-widget-host"
              style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
              <GexProfileWidget
                windowId={WIN.gexProfile}
                onRemove={noop}
                lockedSymbol={symbol}
              />
            </div>
          )}
          {active === 'niners' && (
            <div
              className="sr-mobile-widget-host"
              style={{ touchAction: 'none', overscrollBehavior: 'contain' }}
            >
              <TDSequentialWidget windowId={WIN.niners} onRemove={noop} />
            </div>
          )}
        </SectionGate>
      </div>
    </div>
  );
}
